import { useActiveCircle } from '@/hooks/use-active-circle';
import { WorkflowStepState } from '@calm/evexia-web-sdk';
import useAppConfig from '@rhg/hooks/use-app-config';
import { useStartWorkflow } from '@rhg/hooks/use-start-workflow';
import { useUpdateWorkflowStep } from '@rhg/hooks/use-update-workflow-step';
import {
    WorkflowIds,
    FtueStoredStepName,
    StepStates,
} from '@rhg/utils/workflows';
import { createContext, ReactNode, useState } from 'react';
import { WorkflowState } from './types';

export const WorkflowContext = createContext<WorkflowState>(undefined!);

export function WorkflowProvider({ children }: { children: ReactNode }) {
    const { NEXT_PUBLIC_FTUE_WORKFLOW_ENABLED: isFtueWorkflowEnabled } =
        useAppConfig();
    const { activeCircle } = useActiveCircle();
    const { updateWorkflowStepAsync } = useUpdateWorkflowStep();
    const { startWorkflowAsync } = useStartWorkflow();
    const [currentStep, setCurrentStep] = useState<string>();

    const storeCurrentStep = (name: string, stepId: string) => {
        sessionStorage.setItem(name, stepId);
    };

    const getCurrentStep = (name: string) => {
        return currentStep || sessionStorage.getItem(name);
    };

    async function startWorkflow(workflowDefnId: string) {
        const response = await startWorkflowAsync({
            workflowDefnId,
        });
        if (response.data?.stepDefn?.defnId) {
            setCurrentStep(response.data.stepDefn.defnId);
            storeCurrentStep(FtueStoredStepName, response.data.stepDefn.defnId);
        }
    }

    async function updateWorkflowStep(stepDefnId: string, isSkipped?: boolean) {
        if (activeCircle && isFtueWorkflowEnabled) {
            const stepState = isSkipped
                ? StepStates.SKIPPED
                : StepStates.COMPLETED;
            try {
                const response = await updateWorkflowStepAsync({
                    workflowDefnId: WorkflowIds.FTUE,
                    stepDefnId,
                    request: {
                        state: stepState as WorkflowStepState,
                    },
                });
                if (response.data?.stepDefn?.defnId) {
                    setCurrentStep(response.data.stepDefn.defnId);
                    storeCurrentStep(
                        FtueStoredStepName,
                        response.data.stepDefn.defnId
                    );
                }
            } catch (error) {
                console.error('Error updating workflow step:', error);
            }
        }
    }

    return (
        <WorkflowContext.Provider
            value={{
                isFtueWorkflowEnabled,
                currentStep,
                getCurrentStep,
                startWorkflow,
                updateWorkflowStep,
            }}
        >
            {children}
        </WorkflowContext.Provider>
    );
}
